import { AllyObserver, AllyObject, AllyBase, AllyEventListener, AllyId } from '.';
import { AllyNode } from '../types';

/**
 * The most central class of the plugin.
 * 
 * Only on instance of this class should be active at any given time.
 * 
 * The most important event and mutation listeners are housed in this class.
 * 
 * An object of this class is created if the init function of the service AllyService is called.
 * 
 * @extends AllyBase
 * @see AllyService
 */
export class AllyRoot extends AllyBase {

  /**
   * The AllyObserver object for the AllyRoot class.
   * 
   * @see AllyObserver
   */
  public observer: AllyObserver;
  /**
   * The AllyEventListener object for the AllyRoot class.
   * 
   * @see AllyEventListener
   */
  public eventListener: AllyEventListener;
  /**
   * The root node of the DOM.
   */
  public node: AllyNode;
  /**
   * The AllyObject for the AllyRoot["node"]
   */
  private root: AllyObject;
  
  constructor() {
    super(AllyBase.bridge);
    this.node = document.querySelector("html") as unknown as AllyNode;
    this.observer = new AllyObserver(this.bridge);
    this.eventListener = new AllyEventListener(this.bridge);
    this.root = new AllyObject(this.node, this.bridge, {isRoot: true, root: this});
    this.observer.createObserver((changes, observer) => {
      const added = new Map<AllyNode, AllyNode>();
      const removed = new Set<AllyNode>();
      changes.forEach((change) => {
        change.addedNodes.forEach((node) => {
          added.set(node, change.target);
        });
        change.removedNodes.forEach((node) => {
          removed.add(node);
        });
      });

      // The pass over an added ancestor already covers every node added below it.
      const topmost = [...added].filter(([node]) => !this.hasAddedAncestor(node, added));
      topmost.forEach(([node, parent]) => {
        let parentObject = this.root.getObjectByNode(parent);
        if (!parentObject) {
          this.registerOrphanedSubtree(parent);
          return;
        }
        let newObject = this.root.getObjectByNode(node);
        if (!newObject) {
          newObject = new AllyObject(node, () => parentObject, {isRoot: true});
        } else if (newObject.parentObject !== parentObject) {
          newObject.reparent(parentObject);
        }
        newObject.runRoot(0);
        newObject.runChildListListeners();
      });

      removed.forEach((node) => {
        if (!node.parentElement) {
          this.root.getObjectByNode(node)?.destroy();
        }
      });
    }, this.root, {
      subtree: true,
      childList: true
    });
    this.observer.createObserver((changes, observer) => {
      let list: AllyNode[] = [];
      changes.forEach((change) => {
        if (change.attributeName && this.root.library.angular.isRippleLoaderDisabled(change.attributeName) && this.root.library.angular.isChip(change.target)) {
          let node = change.target
          if (node.setAttribute) {
            let self = this;
            let callback = node.setAttribute.bind(change.target);
            node.setAttribute = (qualifiedName: string, value: string) => {
              if (!(self.root.library.angular.isRippleLoaderDisabled(qualifiedName) && (!node.getAttribute || node.getAttribute(qualifiedName) === value))) {
                callback(qualifiedName, value);
              }
            };
          }
        }
        list.push(change.target);
      });
      (new Set(list)).forEach((node) => {
        let object = this.root.getObjectByNode(node);
        if (object) {
          object.runAttributeListeners();
        }
      });
    }, this.root, {
      subtree: true,
      attributes: true
    });
    this.root.runRoot(0);
  }

  private hasAddedAncestor(node: AllyNode, added: Map<AllyNode, AllyNode>): boolean {
    for (let ancestor = node.parentNode; ancestor; ancestor = ancestor.parentNode) {
      if (added.has(ancestor)) {
        return true;
      }
    }
    return false;
  }

  /** Registers an unknown subtree below the nearest ancestor that has a AllyObject. */
  private registerOrphanedSubtree(node: AllyNode) {
    let top = node;
    let anchor = this.root.getObjectByNode(top.parentNode);
    while (!anchor && top.parentNode) {
      top = top.parentNode;
      anchor = this.root.getObjectByNode(top.parentNode);
    }
    if (!anchor) {
      return;
    }
    const anchorObject = anchor;
    const object = new AllyObject(top, () => anchorObject, {isRoot: true});
    console.debug(`[Ally] registered orphaned subtree <${top.nodeName.toLowerCase()}>`);
    object.runRoot(0);
    object.runChildListListeners();
  }
}
